/*
  Pinned product for collection pages.

  Accepted link formats (all on a collection URL):
    ?first=<product-handle>&variant=<variant-id>&limit=16
    ?first=<full product URL, URL-encoded, may contain ?variant=...>&limit=16   (Meta catalog deep link)
    ?pin=<product-handle>&pin_variant=<variant-id>                              (short alias)

  Puts that product first in the grid, showing the variant's image, color label and link.
  limit = optional cap on how many products show on the page.
  Only runs on page 1. The rest of the collection keeps its normal order.
*/
(() => {
  const HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/i;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let pinnedNode = null;
  let pinnedProductId = null;
  let limit = 0;

  function readParams() {
    const p = new URLSearchParams(window.location.search);
    const page = parseInt(p.get('page') || '1', 10);
    if (page > 1) return null;

    let raw = (p.get('first') || p.get('pin') || '').trim();
    let variant = (p.get('pin_variant') || p.get('variant') || '').trim();
    let handle = raw;

    // `first` may be a full product URL (Meta catalog {{product.url}}), possibly encoded twice.
    if (/%2F|%3A/i.test(raw)) {
      try { raw = decodeURIComponent(raw); } catch (e) {}
    }
    if (raw.includes('/products/')) {
      try {
        const u = new URL(raw, window.location.origin);
        const m = u.pathname.match(/\/products\/([^/?#]+)/);
        handle = m ? m[1] : '';
        const v = u.searchParams.get('variant');
        if (v) variant = v;
      } catch (e) {
        return null;
      }
    }

    handle = decodeURIComponent(handle || '').toLowerCase();
    if (!HANDLE_RE.test(handle)) return null;
    const lim = parseInt(p.get('limit') || '0', 10);
    return {
      handle,
      variant: /^\d+$/.test(variant) ? variant : '',
      limit: lim > 0 ? lim : 0
    };
  }

  function sizedUrl(src, width) {
    const u = new URL(src, window.location.href);
    u.searchParams.set('width', width);
    return u.toString();
  }

  async function buildCard({ handle, variant }) {
    const productUrl = root + 'products/' + encodeURIComponent(handle);
    const [htmlRes, jsonRes] = await Promise.all([
      fetch(productUrl + '?view=pin-card' + (variant ? '&variant=' + variant : '')),
      fetch(productUrl + '.js')
    ]);
    if (!htmlRes.ok || !jsonRes.ok) return null;

    const doc = new DOMParser().parseFromString(await htmlRes.text(), 'text/html');
    const item = doc.querySelector('li[data-pinned-product]');
    if (!item) return null;
    const product = await jsonRes.json();

    const v = variant ? product.variants.find((x) => String(x.id) === variant) : null;
    if (v) {
      const variantUrl = productUrl + '?variant=' + v.id;
      item.querySelectorAll('a[href*="/products/"]').forEach((a) => a.setAttribute('href', variantUrl));

      const img = v.featured_image && v.featured_image.src;
      const firstImg = item.querySelector('img');
      if (img && firstImg) {
        firstImg.src = sizedUrl(img, 800);
        firstImg.srcset = [400, 600, 800, 1000, 1200].map((w) => sizedUrl(img, w) + ' ' + w + 'w').join(', ');
        firstImg.loading = 'eager';
        if (v.featured_image.alt) firstImg.alt = v.featured_image.alt;
      }

      const colorIndex = (product.options || []).findIndex(
        (o) => String((o && o.name) || o).toLowerCase() === 'color'
      );
      const label = item.querySelector('[id="' + product.id + '-color-label"]');
      if (label && colorIndex > -1 && v.options[colorIndex]) label.textContent = v.options[colorIndex];
    }
    return { item, productId: String(product.id) };
  }

  function applyLimit(grid) {
    if (!limit) return;
    Array.from(grid.children).slice(limit).forEach((li) => li.remove());
  }

  function insert(grid) {
    if (!pinnedNode || !grid) return;
    const first = grid.firstElementChild;
    if (first && first.hasAttribute('data-pinned-product')) return;
    grid.querySelectorAll('li[data-pinned-product]').forEach((li) => li.remove());
    grid
      .querySelectorAll('li[data-product-id="' + pinnedProductId + '"]')
      .forEach((li) => li.remove());
    grid.prepend(pinnedNode.cloneNode(true));
    applyLimit(grid);
  }

  async function init() {
    const params = readParams();
    if (!params) return;
    limit = params.limit;
    const built = await buildCard(params).catch(() => null);
    if (!built) return;

    pinnedNode = document.importNode(built.item, true);
    pinnedProductId = built.productId;
    const findGrid = () => document.querySelector('ul.product-grid');
    insert(findGrid());

    // If the grid is re-rendered (sort/filter) and the pin is still in the URL, re-pin it.
    const container = document.querySelector('results-list') || document.body;
    new MutationObserver(() => {
      if (readParams()) insert(findGrid());
    }).observe(container, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
